// src/pages/Reports/ExpirationsReport.js
import React, { useEffect, useRef, useState } from "react";
import { Container, Table, Row, Col, Button, Spinner } from "react-bootstrap";
import Select from "../../components/ReportSelect";
import axios from "axios";
import Pagination from "../../components/Pagination";
import "../../styles/Reports.css";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const formatLocalDate = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

const today = new Date();

const past = new Date(today.getFullYear(), today.getMonth() - 1, 1);
const lastDayOfPastMonth = new Date(
  past.getFullYear(), past.getMonth() + 1, 0).getDate();
past.setDate(Math.min(today.getDate(), lastDayOfPastMonth));
const pastStr = formatLocalDate(past);

const future = new Date(today);
future.setDate(future.getDate() + 60);
const futureStr = formatLocalDate(future);

const expirationDisplay = (value) => {
  const days = Number(value);
  if (!Number.isFinite(days)) return { text: "", className: "" };
  if (days < 0) return { text: "Expired", className: "expiration-days expiration-days--expired" };
  if (days <= 60) return { text: String(days), className: "expiration-days expiration-days--warning" };
  return { text: String(days), className: "expiration-days" };
};

const statusToOption = (status) => ({
  label: status.s_TranslatedText || status.s_name || "",
  value: status.s_id,
});

const getAssociateStatusSelection = (statuses) => {
  const associate = (Array.isArray(statuses) ? statuses : []).find((status) =>
    String(status.s_TranslatedText || status.s_name || "").trim().toLowerCase() === "associate");
  return associate ? [statusToOption(associate)] : [];
};

export default function ExpirationsReport() {
  const navigate = useNavigate();
  const tableRef = useRef(null);

  // dropdowns
  const [dropdowns, setDropdowns] = useState({
    orgUnits: [],
    categories: [],
    subCategories: [],
    groups: [],
    statuses: [],
    docTypes: [],
  });

  // pending (UI controls)
  const [pStartDate, setPStartDate] = useState(pastStr);
  const [pEndDate, setPEndDate] = useState(futureStr);
  const [pDocTypes, setPDocTypes] = useState([]); // [{label, value}]
  const [pOrgUnits, setPOrgUnits] = useState([]);
  const [pCategory, setPCategory] = useState([]); // [{label, value}]
  const [pSubCategory, setPSubCategory] = useState([]);
  const [pGroups, setPGroups] = useState([]);
  const [pStatus, setPStatus] = useState([]); // [{label, value}]
  const [pQuery, setPQuery] = useState(""); // (optional) Search, shown on second row

  // data/runtime
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // fetch dropdowns (Applicant Type, Status, Doc Types)
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [org, cat, subCat, groups, stat, docs] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
          axios.get(`${BASE_URL}/api/reports/expirations/doc-types`, {
            headers,
          }),
        ]);

        const statuses = Array.isArray(stat.data) ? stat.data : [];
        setDropdowns({
          orgUnits: Array.isArray(org.data) ? org.data : [],
          categories: Array.isArray(cat.data) ? cat.data : [],
          subCategories: Array.isArray(subCat.data) ? subCat.data : [],
          groups: Array.isArray(groups.data) ? groups.data : [],
          statuses,
          docTypes: Array.isArray(docs.data)
            ? docs.data.map((d) => ({ label: d, value: d }))
            : [],
        });
        setPStatus((current) =>
          current.length ? current : getAssociateStatusSelection(statuses));
      } catch (err) {
        console.error("Dropdown fetch failed", err);
        setDropdowns({
          orgUnits: [],
          categories: [],
          subCategories: [],
          groups: [],
          statuses: [],
          docTypes: [],
        });
      }
    };
    fetchDropdowns();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setHasSearched(true);
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const params = {
        startDate: pStartDate || undefined,
        endDate: pEndDate || undefined,
        docTypes: pDocTypes.map((d) => d.value).join(",") || undefined,
        orgUnit: pOrgUnits.map((o) => o.value).join(",") || undefined,
        category: pCategory.map((c) => c.value).join(",") || undefined,
        subCategory:
          pSubCategory.map((s) => s.value).join(",") || undefined,
        group: pGroups.map((g) => g.value).join(",") || undefined,
        status: pStatus.map((s) => s.value).join(",") || undefined,
        search: (pQuery || "").trim() || undefined,
      };

      const res = await axios.get(`${BASE_URL}/api/reports/expirations`, {
        headers,
        params,
      });

      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Expirations fetch error:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setPStartDate(pastStr);
    setPEndDate(futureStr);
    setPDocTypes([]);
    setPOrgUnits([]);
    setPCategory([]);
    setPSubCategory([]);
    setPGroups([]);
    setPStatus(getAssociateStatusSelection(dropdowns.statuses));
    setPQuery("");
    setData([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  const sortConfig = useClientTableSort(tableRef, () => setCurrentPage(1));
  const sortedData = sortRowsByConfig(data, sortConfig, [
    (row) => row.userid_no,
    (row) => row.first_name,
    (row) => row.last_name,
    (row) => row.org_unit,
    (row) => row.document_type,
    (row) => row.expiry_date,
    (row) => row.days_to_expiration,
  ]);
  const paged = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const exportExcel = async () => {
    if (data.length === 0) {
      alert("No data to export");
      return;
    }
    const excelModule = await import("exceljs");
    const ExcelJS = excelModule.default || excelModule;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Expirations");
    worksheet.columns = [
      { width: 16 }, { width: 22 }, { width: 22 }, { width: 25 }, { width: 32 }, { width: 16 }, { width: 22 },
    ];
    worksheet.pageSetup = { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
    const headers = ["Employee ID", "First Name", "Last Name", "Org Unit", "Document Type", "Expiry Date", "Days To Expiration"];
    const border = { top: { style: "thin", color: { argb: "FFB8C2C8" } }, left: { style: "thin", color: { argb: "FFB8C2C8" } }, bottom: { style: "thin", color: { argb: "FFB8C2C8" } }, right: { style: "thin", color: { argb: "FFB8C2C8" } } };
    const grouped = new Map();
    data.forEach((record) => {
      const orgUnit = String(record.org_unit || "Unassigned").trim() || "Unassigned";
      if (!grouped.has(orgUnit)) grouped.set(orgUnit, []);
      grouped.get(orgUnit).push(record);
    });
    const groups = [...grouped.entries()].sort(([left], [right]) => left === right ? 0 : left === "Unassigned" ? 1 : right === "Unassigned" ? -1 : left.localeCompare(right));
    groups.forEach(([orgUnit, records], groupIndex) => {
      records.sort((left, right) =>
        Number(left.days_to_expiration) - Number(right.days_to_expiration)
        || String(left.expiry_date || "").localeCompare(String(right.expiry_date || ""))
        || String(left.last_name || "").localeCompare(String(right.last_name || ""))
        || String(left.first_name || "").localeCompare(String(right.first_name || ""))
      );
      if (groupIndex > 0) worksheet.addRow([]);
      const titleRow = worksheet.addRow([orgUnit]);
      worksheet.mergeCells(titleRow.number, 1, titleRow.number, 7);
      titleRow.height = 24;
      titleRow.getCell(1).font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
      titleRow.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF157476" } };
      titleRow.getCell(1).alignment = { vertical: "middle" };
      const headerRow = worksheet.addRow(headers);
      headerRow.height = 22;
      headerRow.eachCell((cell) => { cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF212529" } }; cell.alignment = { vertical: "middle" }; cell.border = border; });
      records.forEach((record) => {
        const expiration = expirationDisplay(record.days_to_expiration);
        const row = worksheet.addRow([record.userid_no ?? "", record.first_name ?? "", record.last_name ?? "", orgUnit, record.document_type ?? "", record.expiry_date ? String(record.expiry_date).substring(0, 10) : "", expiration.text]);
        row.eachCell({ includeEmpty: true }, (cell) => { cell.border = border; cell.alignment = { vertical: "middle" }; });
        const daysCell = row.getCell(7);
        if (Number(record.days_to_expiration) < 0) { daysCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8D7DA" } }; daysCell.font = { bold: true, color: { argb: "FF842029" } }; }
        else if (Number(record.days_to_expiration) <= 60) { daysCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3CD" } }; daysCell.font = { bold: true, color: { argb: "FF664D03" } }; }
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "ExpirationsReport.xlsx");
  };

  return (
    <Container className="form-wrapper my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-calendar-times" /></span><span>Expirations</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filter row — matches your Time & Attendance pattern */}
      <ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={2}>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            placeholder="yyyy-mm-dd"
            value={pStartDate}
            onChange={(e) => setPStartDate(e.target.value)}
          />
        </Col>

        <Col md={2}>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            placeholder="yyyy-mm-dd"
            value={pEndDate}
            onChange={(e) => setPEndDate(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <label>Document Types</label>
          <Select
            isMulti
            options={dropdowns.docTypes}
            value={pDocTypes}
            onChange={setPDocTypes}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>

        <Col md={2}>
          <label>Status</label>
          <Select
            isMulti
            options={(dropdowns.statuses || []).map(statusToOption)}
            value={pStatus}
            onChange={setPStatus}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
      </Row>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Org Units</label>
          <Select
            isMulti
            options={(dropdowns.orgUnits || []).map((o) => ({
              label: o.ou_name,
              value: o.ou_id,
            }))}
            value={pOrgUnits}
            onChange={setPOrgUnits}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col md={3}>
          <label>Employment Categories</label>
          <Select
            isMulti
            options={(dropdowns.categories || []).map((c) => ({
              label: c.c_name,
              value: c.c_id,
            }))}
            value={pCategory}
            onChange={setPCategory}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col md={3}>
          <label>Employment Sub-Category</label>
          <Select
            isMulti
            options={(dropdowns.subCategories || []).map((s) => ({
              label: s.sc_name,
              value: s.sc_id,
            }))}
            value={pSubCategory}
            onChange={setPSubCategory}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col md={3}>
          <label>Groups</label>
          <Select
            isMulti
            options={(dropdowns.groups || []).map((g) => ({
              label: g.pg_name,
              value: g.pg_id,
            }))}
            value={pGroups}
            onChange={setPGroups}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
      </Row>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search</label>
          <input
            type="text"
            className="form-control"
            placeholder="ID / Name"
            value={pQuery}
            onChange={(e) => setPQuery(e.target.value)}
          />
        </Col>
      </Row>
      </ReportFilterPanel>
      {/* Filter + Clear */}
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto" className="align-end">
          <div className="button-group tight">
            <Button variant="primary" onClick={fetchData}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>
        {/* Export pinned to the far right */}
        <Col md="auto" className="align-end ms-auto">
          <Button
            className="btn-export"
            onClick={exportExcel}
            disabled={!hasSearched || data.length === 0}
          >
            Export Excel
          </Button>
        </Col>
      </Row>

      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table
            ref={tableRef}
            data-nw-client-sort="true"
            bordered
            hover
            size="sm"
            className="compact-table"
          >
            <thead className="table-dark text-center">
              <tr>
                <th>EMPLOYEE ID</th>
                <th>FIRST NAME</th>
                <th>LAST NAME</th>
                <th>ORG UNIT</th>
                <th>DOCUMENT TYPE</th>
                <th>EXPIRY DATE</th>
                <th>DAYS TO EXPIRATION</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paged.map((r, i) => (
                  <tr key={`${r.user_id}-${r.document_type}-${i}`}>
                    <td>{r.userid_no || ""}</td>
                    <td>{r.first_name || ""}</td>
                    <td>{r.last_name || ""}</td>
                    <td>{r.org_unit || ""}</td>
                    <td>{r.document_type}</td>
                    <td>
                      {r.expiry_date
                        ? String(r.expiry_date).substring(0, 10)
                        : ""}
                    </td>
                    <td>{r.days_to_expiration}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={sortedData.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
